/** Chronicle lines, alarms and their codes in English (ADR 0025): the same texts on screen
 *  (web, i18next) and on paper (the PDF of the report, i18next on the server). */
export const chronicleEn = {
  kinds: {
    alarm: 'alarm',
    status: 'status',
    assignment: 'assignment',
    communication: 'communication',
    media: 'media',
    position: 'position',
    resource: 'resource',
    event: 'event',
    note: 'note',
  },
  events: {
    operation_opened: 'Operation opened · {{level}} level',
    operation_closed: 'Operation closed · chronicle sealed',
    alert_level_changed: 'Alert level from {{from}} to {{to}}',
    sectors_assigned: '{{count}} sectors assigned to teams {{teams}}',
    drone_in_flight: 'Drone flying over sector {{sector}}, altitude {{altitude}} m',
    coc_gym_available: 'Municipal gym available as assembly point',
    team_assigned_sector: 'Team {{team}} assigned to sector {{sector}}',
    sector_completed: 'Sector {{sector}} completed at {{pct}}%',
    node_unreachable: 'Node {{node}} unreachable',
    team_returning: 'Team {{team}} returning to the command post',
    photo_from_field: 'Photo from the field — {{note}}',
    battery_critical: 'Field battery at {{pct}}%',
    position_updated: 'Position updated in sector {{sector}}, quadrant {{quadrant}}',
    sos_received: 'SOS received from sector {{sector}}',
    post_opened: 'Fixed post opened · {{level}} level',
    bulletin_received: 'Regional bulletin received',
    hourly_reading: 'Hourly reading recorded: {{value}}',
    hydro_level: 'Water level {{value}}',
    drill_opened: 'Drill opened · {{level}} level',
    base_camp_ready: 'Base camp completed',
    triage_ready: 'Triage set up, {{count}} role players registered',
    alarm_acknowledged: '{{code}} acknowledged by {{by}}',
    alarm_closed: '{{code}} closed',
    team_status_changed: 'Team {{team}}: new state “{{status}}”',
    team_created: 'Team {{team}} ({{code}}) created · people: {{people}}',
    team_tracker_assigned: 'Tracker {{device}} given to team {{team}}',
    team_transferred_in: 'Team {{team}} ({{code}}) arrived from {{from}}',
    team_transferred_out: 'Team {{team}} ({{code}}) moved to {{to}}',
    team_headcount_changed: 'Team {{team}}: people from {{from}} to {{to}}',
    team_dogs_changed: 'Team {{team}}: dogs from {{from}} to {{to}}',
    team_sector_changed: 'Team {{team}}: sector {{sector}}',
    team_sector_cleared: 'Team {{team}}: sector to assign',
    team_radio_changed: 'Team {{team}}: radio channel {{channel}}',
    team_radio_cleared: "Team {{team}}: the mission's radio channel",
    team_member_added: '{{person}} joins team {{team}}',
    team_member_removed: '{{person}} leaves team {{team}}',
    team_tracker_released: 'Tracker {{device}} taken from team {{team}}',
    team_dissolved: 'Team {{team}} ({{code}}) dissolved',
    team_restored: 'Team {{team}} ({{code}}) put back',
    manual_note: '{{text}}',
    unknown: 'Event {{key}}',
    resource_assigned: 'Resource {{code}} · {{name}} assigned to the operation',
    resource_released: 'Resource {{code}} · {{name}} removed from the operation',
    map_area_drawn: '{{code}} “{{name}}” drawn on the map',
    map_grid_created: 'Search grid {{code}}: {{cells}} cells of {{cellM}} m',
    map_grid_removed: 'Search grid {{code}} removed ({{cells}} cells)',
    map_sector_assigned: 'Sector {{sector}} assigned to {{team}}',
    map_sector_unassigned: 'Sector {{sector}} without a team',
    map_element_deleted: '{{code}} “{{name}}” deleted from the map',
    map_element_restored: '{{code}} “{{name}}” restored on the map',
    station_unreachable: 'Station {{station}} unreachable',
    alarm_resolved: '{{code}} cleared: {{subject}} reachable again',
    station_band_full: 'Station {{station}} online on {{link}}, full bandwidth',
    station_band_reduced:
      'Station {{station}} on {{link}}: reduced bandwidth, drone video and radar paused',
    station_band_minimal:
      'Station {{station}} on {{link}}: minimal bandwidth, video attachments paused too',
    station_band_offline: 'Station {{station}} without a link',
    field_check_in: '{{who}}: all OK',
    field_report_person_found: 'Report from the field · person found: {{text}}',
    field_report_hazard: 'Report from the field · hazard: {{text}}',
    field_report_damage: 'Report from the field · damage: {{text}}',
    field_report_resource_needed: 'Report from the field · resource needed: {{text}}',
    field_report_other: 'Report from the field: {{text}}',
    field_sos_raised: 'SOS from {{operator}} with Sentinel Field',
    field_sos_false_alarm:
      '{{operator}} says the SOS was a mistake: the alarm stays open until the operations room closes it',
    field_phone_revoked: 'Phone {{code}} «{{label}}» disconnected from Sentinel Field',
    sensor_ordinary: '{{sensor}} at {{value}} {{unit}}: ordinary threshold exceeded',
    sensor_moderate: '{{sensor}} at {{value}} {{unit}}: moderate threshold exceeded',
    sensor_high: '{{sensor}} at {{value}} {{unit}}: high threshold exceeded',
    sensor_back_below: '{{sensor}} at {{value}} {{unit}}: back under the thresholds',
    operation_opened_continuing: 'Operation opened · {{level}} level · continues {{continues}}',
    operation_closed_outcome:
      'Operation closed · $t(chronicle.outcomes.{{outcome}}) · chronicle sealed',
    reference_point_set:
      'Reference point set · {{coords}} · $t(chronicle.referenceSources.{{source}})',
    reference_point_set_device:
      'Reference point set · {{coords}} · device position ± {{accuracyM}} m',
    reference_point_removed: 'Reference point removed: position to be set',
    area_laid:
      'Area laid on the point · radius {{radius}} · $t(chronicle.sectorsCount, {"count": {{sectors}} })',
  },
  referenceSources: {
    search: 'from the search',
    map: 'chosen on the map',
    device: 'device position',
  },
  outcomes: {
    positive: 'positive outcome',
    negative: 'negative outcome',
    concluded: 'response completed',
    drill_concluded: 'drill completed',
    cancelled: 'cancelled',
  },
  sectorsCount_zero: 'no sectors',
  sectorsCount_one: '{{count}} sector',
  sectorsCount_other: '{{count}} sectors',
  alarms: {
    sos_operator: '{{operator}}, {{code}}, sector {{sector}}. Requires nominal acknowledgement.',
    battery_critical: '{{team}} at {{pct}}%. Plan a swap or the return within {{minutes}} minutes.',
    node_offline: '{{node}} silent for {{minutes}} minutes. Sector {{sector}} is uncovered.',
    sensor_threshold: '{{sensor}} at {{value}}, {{gap}} below the attention threshold.',
    external_alert: '{{source}}: ordinary criticality confirmed.',
    no_criticality: 'No active issue. Next scenario at {{next}}.',
    unknown: 'Alarm {{key}}',
    station_offline:
      '{{station}} silent for {{minutes}} minutes: the command post may have lost its link.',
    field_sos:
      '{{operator}} ({{code}}) asked for help from Sentinel Field. Requires nominal acknowledgement.',
    sensor_ordinary: '{{sensor}} at {{value}} {{unit}}, above the ordinary threshold.',
    sensor_moderate: '{{sensor}} at {{value}} {{unit}}, above the moderate threshold.',
    sensor_high: '{{sensor}} at {{value}} {{unit}}, above the high threshold.',
  },
  codes: {
    'ALL-01': 'Operator SOS',
    'ALL-02': 'Man down',
    'ALL-03': 'Missed radio check-in',
    'ALL-04': 'GPS contact lost',
    'ALL-05': 'Left the operational zone',
    'ALL-06': 'Critical resource battery',
    'ALL-07': 'Node or gateway offline',
    'ALL-08': 'Sensor threshold exceeded',
    'ALL-09': 'Drone: link lost or return',
    'ALL-10': 'LoRa coverage degradation',
    'ALL-11': 'External alert',
    'ALL-12': 'Sentinel service degraded',
  },
  level: 'L{{level}}',
  status: {
    open: 'Open',
    acknowledged: 'Acknowledged',
    closed: 'Closed',
  },
} as const;
